import type { PreferredStem } from '@/api/preferences';
import { CaseStatus } from '@/api/caseSearch';
import { Side } from '@/domain/anatomy/side';
import { PatientSex } from '@/domain/anatomy/sex';
import { type AxiosRequestConfig, type AxiosResponse, HttpStatusCode } from 'axios';
import { client } from '@/api/http';
import { AppError } from '@/stores/appErrorStore';
import { errorDetail } from '@/api/errorDetail';
import type { ImageModality } from '@/api/study/study';

export interface OptionsApiResponse {
    options: CaseSettingsOptions;
}

export type CaseApiPatient = {
    first_name: string;
    middle_name: string;
    family_name: string;
    birth_date: string;
    sex: PatientSex | '';
};

export type CaseSpecificationsApiObject = {
    preferred_system: string;
    stems: PreferredStem[];
    target_leg_length_change: number;
    target_offset_change: number;
    cup_anteversion_mode: string;
    cup_anteversion_angle: number | null;
    cup_inclination_angle: number;
    cup_fit_method: string;
    cup_align_mode: string;
};

export type CaseSettingsApiObjectBase = {
    id: number | null;
    owner_id: number | null;
    web_component_version: string;
    name: string;
    description: string;
    side: Side | '';
    surgery_date: string;
    surgeon_id: number | null;
    patient: CaseApiPatient;
    surgical_specifications: CaseSpecificationsApiObject;
};

export type CaseSettingsApiObjectCreate = Omit<CaseSettingsApiObjectBase, 'owner_id'>;

export type CaseDetailsApiObjectBase = {
    id: number | null;
    web_component_version: string;
    side: Side | '';

    name: string;
    description: string;
    status: CaseStatus;

    notifications: CaseNotification[];

    study: number | null;

    hip_spine_study_id: number | null;

    case_version: number;
};

export type CaseNotificationLevelType = 'Info' | 'Warning' | 'Error';
export type CaseNotification = {
    message: string;
    /**
     * This timestamp is an ISO-8601 timestamp of the form:
     *     "2019-12-09T01:50:36.756542+00:00"
     */
    timestamp: string;
    type: CaseNotificationLevelType;
    study: number | null;
};

export interface SurgeonOption {
    id: number;
    name: string;
}

/**
 * These are the list of available options to populate the dropdowns / multiple checkbox selection
 */
export interface CaseSettingsOptions {
    stems: PreferredStem[];
    surgeons: SurgeonOption[];
}

export function formatCaseApiPatient(patient: CaseApiPatient | null): string {
    if (patient) {
        if (patient.family_name && patient.first_name && patient.middle_name) {
            return `${patient.first_name} ${patient.middle_name} ${patient.family_name}`;
        } else if (patient.family_name && patient.first_name) {
            return `${patient.first_name} ${patient.family_name}`;
        } else if (patient.family_name) {
            return `${patient.family_name}`;
        } else if (patient.first_name) {
            return `${patient.first_name}`;
        }
    }
    return '';
}

export type ApiCaseStudy = {
    /**
     * The name of the series
     */
    readonly name: string;

    /**
     * A series identifier
     */
    readonly series: string;

    /**
     * The number of files in the series. This is set by the client when the study is created. The client
     * then creates those files in the context of the study. The client must ensure that the study has the
     * correct number of files.
     */
    readonly file_count: number;

    readonly source_modality: ImageModality;
};

export type PartialCaseApiObjectBase = {
    id: number;
    owner_id: number | null;
    web_component_version: string;
    name: string;
    description: string;
    status: CaseStatus;
    side: Side;
    surgery_date: string | null;
    surgeon_id: number | null;
    patient: CaseApiPatient | null;
    surgical_specifications: CaseSpecificationsApiObject | null;
    study: ApiCaseStudy | null;
};

export type CaseSettingsApiObject = CaseSettingsApiObjectBase & {
    study: ApiCaseStudy | null;
};

export async function getCaseSettings(
    id: number,
    config?: AxiosRequestConfig,
): Promise<CaseSettingsApiObject> {
    return client.get(`/cases/${id}/settings`, config).then(({ status, data }: AxiosResponse) => {
        if (status === HttpStatusCode.Ok) {
            return data;
        } else if (status === HttpStatusCode.NotFound || status === HttpStatusCode.Forbidden) {
            throw new AppError(
                'Case not found' + errorDetail(data),
                'This case does not exist or you do not have permission to view it.',
            );
        }

        throw Error(`Failed to load case settings: ${data?.detail}`);
    });
}
